import React, { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { bemCssModules } from '@utils';
import { useLocation, useNavigate } from '@utils/hooks';

import { useStore } from '@modules';
import { VerificationStatusesEnum } from '@modules/user/types';

import Banner from './Banner';
import { Carrot1, Carrot2, Head, HeadMobile, SecurityMobile } from './images';

import style from './Banners.scss';

const cn = bemCssModules(style, 'Banners');
const { incomplete, under_review, complete, failed } = VerificationStatusesEnum;

const imagesKyc = [<Head />, '/img/verify_identity_banner.png'];
const imagesCard = ['/img/card_banner.png'];
const imagesInviteFriends = [<Carrot1 />, '/img/invite_friends_banner.png', <Carrot2 />];
const imagesSecurity = ['/img/security_banner.png'];

export type TBanners = {
  additional?: React.ReactNode;
};

const Banners: CustomFC<TBanners> = ({ className, additional }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [{ user }] = useStore();
  const { data } = user;
  const kycStatus = data?.checks.document;
  const strongSecurity = data?.strong_security;
  const { pathname } = useLocation();

  const mobileImages = (...sources: ReactNode[]) => kycStatus !== complete && sources;

  const redirectToVerifyIdentity = () =>
    navigate<'verifyIdentity'>(CONST.nav.verifyIdentity, { state: { from: pathname } });

  return (
    <div className={cn({ mobileScroll: kycStatus === complete }, [className])}>
      {kycStatus === under_review && (
        <Banner
          title={t('portfolio.banners.verification_in_progress_title')}
          subtitle={t('portfolio.banners.verification_in_progress_sub_title')}
          onClick={redirectToVerifyIdentity}
          isLoading
        />
      )}
      {kycStatus === incomplete && (
        <Banner
          title={t('kyc_intro.h1')}
          images={imagesKyc}
          mobileImages={mobileImages(<HeadMobile />, '/img/verify_identity_banner_mobile.png')}
          onClick={redirectToVerifyIdentity}
        />
      )}
      {kycStatus === failed && (
        <Banner
          title={t('public.verification_incomplete_description')}
          images={imagesCard}
          // TODO: fix mobile images
          mobileImages={mobileImages('/img/card_banner_mobile.png')}
          onClick={redirectToVerifyIdentity}
        />
      )}

      {additional}

      <Banner
        className={cn('banner')}
        title={t('portfolio.banners.invite_friends_banner_text')}
        images={imagesInviteFriends}
        // TODO: fix mobile images
        mobileImages={mobileImages('/img/invite_friends_banner_mobile.png')}
        bottomImage
        onClick={() => navigate(CONST.nav.invite)}
      />
      {!strongSecurity && pathname !== CONST.nav.profileEdit && (
        <Banner
          className={cn('banner')}
          title={t('portfolio.banners.high_security_banner_text')}
          images={imagesSecurity}
          mobileImages={mobileImages(<SecurityMobile />, '/img/security_banner_mobile.png')}
          onClick={() =>
            navigate<'profileEdit'>(CONST.nav.profileEdit, { state: { from: pathname } })
          }
        />
      )}
    </div>
  );
};

export default Banners;
